/**
 * PLA-142: Active V3 Pipeline Hook
 * 
 * Queries for user's active V3 pipeline and provides state for upload locking.
 * Subscribes to realtime updates and polls ONLY when an active pipeline exists.
 * 
 * Polling Strategy:
 * - Initial load on mount to check for existing active pipeline
 * - Poll every 5s ONLY while an active pipeline exists (for progress updates)
 * - Stop polling when no active pipeline (rely on realtime for new uploads)
 * - Realtime subscription handles INSERT/UPDATE events
 * 
 * Stage 2: Frontend State Management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import {
  type V3UploadState,
  type ActivePipelineRun,
  deriveV3UploadState,
  getV3UploadStateConfig,
  isUploadDisabled,
  ACTIVE_PIPELINE_STATUSES,
  UPLOAD_DISABLED_COPY,
} from '@/types/v3-upload-state';

interface UseActiveV3PipelineOptions {
  /** Polling interval in ms when active pipeline exists (default: 5000) */
  pollingInterval?: number;
  /** Enable realtime updates (default: true) */
  enableRealtime?: boolean;
  /** Enable hook (default: true) */
  enabled?: boolean;
}

interface UseActiveV3PipelineResult {
  /** Whether user has an active V3 pipeline */
  hasActivePipeline: boolean;
  /** Current UX state derived from backend */
  uxState: V3UploadState;
  /** Active pipeline run data (null if none) */
  activePipeline: ActivePipelineRun | null;
  /** State configuration for UI rendering */
  stateConfig: ReturnType<typeof getV3UploadStateConfig>;
  /** Whether upload should be disabled */
  uploadDisabled: boolean;
  /** Copy to show when upload is disabled */
  disabledCopy: string;
  /** Loading state */
  isLoading: boolean;
  /** Error if query failed */
  error: Error | null;
  /** Manually refresh pipeline state */
  refresh: () => Promise<void>;
}

const DEFAULT_POLLING_INTERVAL = 5000; // 5 seconds for active pipeline monitoring

/**
 * Hook to track active V3 pipeline for the current user.
 * Returns state needed for upload locking and progress display.
 */
export function useActiveV3Pipeline(
  options?: UseActiveV3PipelineOptions
): UseActiveV3PipelineResult {
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    enableRealtime = true,
    enabled = true,
  } = options ?? {};

  const [activePipeline, setActivePipeline] = useState<ActivePipelineRun | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  /**
   * Query for active pipeline.
   * An active pipeline has status IN ('uploaded', 'processing').
   */
  const loadActivePipeline = useCallback(async () => {
    if (!enabled) return;

    try {
      console.log('[useActiveV3Pipeline] Checking for active pipeline...');

      const { data, error: queryError } = await supabase
        .from('swing_runs')
        .select('id, status, analysis_stage, analysis_progress, created_at')
        .in('status', ACTIVE_PIPELINE_STATUSES)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (queryError) {
        console.error('[useActiveV3Pipeline] Query error:', queryError);
        throw queryError;
      }

      if (!mountedRef.current) return;

      if (data) {
        console.log('[useActiveV3Pipeline] Active pipeline found:', {
          id: data.id,
          status: data.status,
          stage: data.analysis_stage,
        });
        setActivePipeline(data as ActivePipelineRun);
      } else {
        console.log('[useActiveV3Pipeline] No active pipeline');
        setActivePipeline(null);
      }

      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('[useActiveV3Pipeline] Error:', errorMessage);
      setError(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [enabled]);

  /**
   * Handle realtime UPDATE event for swing_runs.
   * Updates local state if the updated run is our active pipeline,
   * or if a run transitions to an active state.
   */
  const handleUpdate = useCallback((payload: any) => {
    const updated = payload.new as ActivePipelineRun;
    console.log('[useActiveV3Pipeline] Realtime UPDATE:', {
      id: updated.id,
      status: updated.status,
      stage: updated.analysis_stage,
    });

    setActivePipeline((prev) => {
      // If this is our current active pipeline
      if (prev && prev.id === updated.id) {
        // Check if it's still active
        if (ACTIVE_PIPELINE_STATUSES.includes(updated.status as any)) {
          return updated;
        } else {
          // Pipeline completed or failed - no longer active
          console.log('[useActiveV3Pipeline] Pipeline completed/failed, clearing');
          return null;
        }
      }

      // If we don't have an active pipeline and this one is active
      if (!prev && ACTIVE_PIPELINE_STATUSES.includes(updated.status as any)) {
        console.log('[useActiveV3Pipeline] New active pipeline detected');
        return updated;
      }

      return prev;
    });
  }, []);

  /**
   * Handle realtime INSERT event.
   * New upload detected - if it's active, track it.
   */
  const handleInsert = useCallback((payload: any) => {
    const inserted = payload.new as ActivePipelineRun;
    console.log('[useActiveV3Pipeline] Realtime INSERT:', {
      id: inserted.id,
      status: inserted.status,
    });

    if (ACTIVE_PIPELINE_STATUSES.includes(inserted.status as any)) {
      setActivePipeline(inserted);
    }
  }, []);

  // Set up Realtime subscription
  useEffect(() => {
    if (!enabled || !enableRealtime) {
      console.log('[useActiveV3Pipeline] Realtime disabled');
      return;
    }

    console.log('[useActiveV3Pipeline] Setting up Realtime subscription...');

    const channel = supabase
      .channel('active_v3_pipeline')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'swing_runs',
        },
        handleUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'swing_runs',
        },
        handleInsert
      )
      .subscribe((status) => {
        console.log('[useActiveV3Pipeline] Realtime status:', status);
      });

    channelRef.current = channel;

    return () => {
      console.log('[useActiveV3Pipeline] Cleaning up Realtime subscription');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, enableRealtime, handleUpdate, handleInsert]);

  // Set up polling ONLY when an active pipeline exists
  // This prevents unnecessary polling when no upload is in progress
  useEffect(() => {
    // Don't start polling until initial load is complete
    if (!enabled || pollingInterval <= 0 || !initialLoadComplete) {
      return;
    }

    // Only poll when there's an active pipeline to track
    if (!activePipeline) {
      // Clear any existing polling when pipeline completes
      if (pollingIntervalRef.current) {
        console.log('[useActiveV3Pipeline] Stopping polling - no active pipeline');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Active pipeline exists - start polling for progress updates
    console.log(`[useActiveV3Pipeline] Starting polling (${pollingInterval}ms) for active pipeline ${activePipeline.id}`);

    const interval = setInterval(() => {
      loadActivePipeline();
    }, pollingInterval);

    pollingIntervalRef.current = interval;

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [enabled, pollingInterval, loadActivePipeline, activePipeline, initialLoadComplete]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      loadActivePipeline();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [enabled, loadActivePipeline]);

  // Derive UX state from active pipeline
  const uxState = deriveV3UploadState(activePipeline);
  const stateConfig = getV3UploadStateConfig(uxState);
  const uploadDisabled = isUploadDisabled(uxState);

  return {
    hasActivePipeline: activePipeline !== null,
    uxState,
    activePipeline,
    stateConfig,
    uploadDisabled,
    disabledCopy: uploadDisabled ? UPLOAD_DISABLED_COPY : '',
    isLoading,
    error,
    refresh: loadActivePipeline,
  };
}

export default useActiveV3Pipeline;
